import { Card, CardContent, FormHelperText, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { backgroundColors } from "../../../utils/charts";
import BulletLegend from "../../charts/BulletLegend";
import { getChartConfig } from "../../charts/getChartConfig";
import BreakdownByGender from "./BreakdownByGender";
import ProjectMetricCardsSkeleton from "./ProjectMetricCardsSkeleton";
import TotalPatientCounts from "./TotalPatientCounts";

export default function ProjectMetricCards({
  data,
  forAdmin = false,
}: {
  data: Partial<DeviceSearchResponse>;
  forAdmin?: boolean;
}) {
  const gendersConfig = useMemo(
    () => getChartConfig(data?.genders ?? []),
    [data.genders],
  );
  const totalPatientsConfig = useMemo(() => {
    return {
      labels: ["Adults (18 and older)", "Pediatric (under 18)"],
      datasets: [
        {
          label: "",
          data: [
            data.totalAdultPatients ?? 0,
            data.totalPediatricPatients ?? 0,
          ],
          backgroundColor: backgroundColors,
        },
      ],
    };
  }, [data.totalAdultPatients, data.totalPediatricPatients]);
  const totalPatientsCount = data.totalPatients ?? 0;
  if (!data || Object.keys(data).length === 0) {
    return <ProjectMetricCardsSkeleton staticSkeleton={forAdmin} />;
  }

  return (
    <section className="project-metric-cards-container">
      <Card className="total-patients">
        <CardContent className="card-content">
          <Typography variant="h6" className="card-title">
            Total Patient Counts
          </Typography>
          <div className="chart-container">
            <TotalPatientCounts data={totalPatientsConfig} />
          </div>
          <div>
            <div className="chart-labels">
              {totalPatientsConfig.labels.map((_, index) => {
                const text = `${totalPatientsConfig.datasets[0].data[index]} ${totalPatientsConfig.labels[index]}`;
                const bulletColor =
                  totalPatientsConfig.datasets[0].backgroundColor[index];
                return (
                  <BulletLegend
                    key={text}
                    text={text}
                    bulletColor={bulletColor}
                    classes={"legend-text"}
                  />
                );
              })}
            </div>
          </div>
          <FormHelperText className="helper-text">
            Total number of patients in 3Aware with procedures in the selected
            time frame.
          </FormHelperText>
        </CardContent>
      </Card>
      <Card className="breakdown-by-gender">
        <CardContent className="card-content">
          <Typography variant="h6" className="card-title">
            Breakdown by Gender
          </Typography>
          <div className="chart-container">
            {gendersConfig && <BreakdownByGender data={gendersConfig} />}
          </div>
          <div>
            <div className="chart-labels">
              {gendersConfig &&
                gendersConfig.labels.map((_, index) => {
                  const numberOfPatients =
                    gendersConfig.datasets[0].data[index];
                  const legendPercentage = (
                    (numberOfPatients / totalPatientsCount) *
                    100
                  ).toFixed(0);
                  const text = `${legendPercentage}% - ${gendersConfig.labels[index]}`;
                  const bulletColor =
                    gendersConfig.datasets[0].backgroundColor[index];
                  return (
                    <BulletLegend
                      key={text}
                      text={text}
                      bulletColor={bulletColor}
                      classes={"legend-text"}
                    />
                  );
                })}
            </div>
          </div>
          <FormHelperText className="helper-text">
            Breakdown of total patient count by gender.
          </FormHelperText>
        </CardContent>
      </Card>
    </section>
  );
}
